import { ReactNode } from 'react';
import { Typography } from '@mui/material';
import { TFunction } from 'i18next';

import {
  formatDate,
  getTimeAgo,
  SHORT_MONTH_DAY_YEAR_12HOUR_FORMAT,
} from 'shared/dates';
import { ExternalGeoEvent } from 'shared/types';
import { formatNumber } from 'shared/utils';
import {
  VoidFunc,
  ExternalGeoEventField,
} from './ExternalGeoEventDetails.types';
import { ExternalSource } from '../../constants';

const ALERT_WEST_CAMERA_URL_BASE = 'https://alertwest.live/cam-console/';

const renderUnits = (event: ExternalGeoEvent, t: TFunction): string => {
  if (event.data.externalSource !== 'pulsepoint') {
    return t('externalGeoEvent.unknown');
  }
  const statuses = Object.entries(event.data.unitsGroupedByStatus ?? {}).map(
    ([key, val]) => `${key}: ${val}`,
  );
  return statuses.join(' | ');
};

const renderAcreage = (event: ExternalGeoEvent, t: TFunction): string =>
  event.data.acreage == null
    ? t('externalGeoEvent.unknown')
    : formatNumber(event.data.acreage);

const renderContainment = (event: ExternalGeoEvent, t: TFunction): string =>
  event.data.containment == null
    ? t('externalGeoEvent.unknown')
    : `${event.data.containment}%`;

const renderFireType = (event: ExternalGeoEvent, t: TFunction): string =>
  t(
    `externalGeoEvent.fireType.${event.data.isPrescribed ? 'prescribed' : 'wildfire'}`,
  );

const renderFlow = (level: number | null, t: TFunction): string =>
  level == null ? t('externalGeoEvent.unknown') : `${level.toFixed(0)} CFS`;

const renderObservedFlow = (event: ExternalGeoEvent, t: TFunction): string => {
  if (event.data.externalSource !== 'noaa_river_gauge') {
    return t('externalGeoEvent.unknown');
  }
  return renderFlow(event.data.observedFlow, t);
};

const renderForecastFlow = (event: ExternalGeoEvent, t: TFunction): string => {
  if (event.data.externalSource !== 'noaa_river_gauge') {
    return t('externalGeoEvent.unknown');
  }
  return renderFlow(event.data.forecastFlow, t);
};

const renderLevel = (level: number | null, t: TFunction): string =>
  level == null ? t('externalGeoEvent.unknown') : `${level.toFixed(1)} ft`;

const renderObservedLevel = (event: ExternalGeoEvent, t: TFunction): string => {
  if (event.data.externalSource !== 'noaa_river_gauge') {
    return t('externalGeoEvent.unknown');
  }
  return renderLevel(event.data.observedLevel, t);
};

const renderForecastLevel = (event: ExternalGeoEvent, t: TFunction): string => {
  if (event.data.externalSource !== 'noaa_river_gauge') {
    return t('externalGeoEvent.unknown');
  }
  return renderLevel(event.data.forecastLevel, t);
};

const renderAccumulation = (event: ExternalGeoEvent, t: TFunction): string => {
  if (
    event.data.externalSource !== 'synoptic_precipitation' ||
    !event.data.observations.length
  ) {
    return t('externalGeoEvent.unknown');
  }

  const latest = event.data.observations.at(-1);
  const interval = event.data.intervalMinutes
    ? `${event.data.intervalMinutes} min`
    : `1 hr`;
  return `${latest?.rawValue.toFixed(2)} in ${interval}`;
};

const renderIntensity = (event: ExternalGeoEvent, t: TFunction): string => {
  if (
    event.data.externalSource !== 'synoptic_precipitation' ||
    !event.data.observations.length
  ) {
    return t('externalGeoEvent.unknown');
  }

  const latest = event.data.observations.at(-1);
  return `${latest?.intensityInHr.toFixed(2)}/hr`;
};

const renderThreshold = (event: ExternalGeoEvent, t: TFunction): string => {
  if (event.data.externalSource !== 'synoptic_precipitation') {
    return t('externalGeoEvent.unknown');
  }
  return `${event.data.threshold.toFixed(2)}/hr`;
};

const renderStation = (event: ExternalGeoEvent, t: TFunction): string => {
  if (event.data.externalSource === 'synoptic_precipitation') {
    return `${event.data.name} (${event.data.stationStid})`;
  }
  return t('externalGeoEvent.unknown');
};

const renderStartTime = (event: ExternalGeoEvent, t: TFunction): string =>
  event.data.dateStart
    ? getTimeAgo(event.data.dateStart)
    : t('externalGeoEvent.unknown');

const renderStartDate = (event: ExternalGeoEvent, t: TFunction): string =>
  event.data.dateStart
    ? formatDate(new Date(event.data.dateStart), 'yyyy-MM-dd')
    : t('externalGeoEvent.unknown');

const renderDatetime = (
  datetime: string | undefined | null,
  t: TFunction,
): string =>
  datetime
    ? formatDate(new Date(datetime), SHORT_MONTH_DAY_YEAR_12HOUR_FORMAT, true)
    : t('externalGeoEvent.unknown');

const renderStartDatetime = (event: ExternalGeoEvent, t: TFunction): string =>
  renderDatetime(event.data.dateStart, t);

const renderEndDatetime = (event: ExternalGeoEvent, t: TFunction): string =>
  'dateEnd' in event.data
    ? renderDatetime(event.data.dateEnd, t)
    : t('externalGeoEvent.unknown');

const renderReported = (event: ExternalGeoEvent, t: TFunction): string => {
  if (event.data.externalSource === 'ngfs') {
    const { eventAge, dateCreated } = event.data;
    if (eventAge) return eventAge;
    if (dateCreated) return getTimeAgo(dateCreated);
  }
  return t('externalGeoEvent.unknown');
};

const renderTemperature = (event: ExternalGeoEvent, t: TFunction): string => {
  if (
    event.data.externalSource !== 'fire_guard' ||
    event.data.temperature == null
  ) {
    return t('externalGeoEvent.unknown');
  }
  return event.data.temperature.toFixed(2);
};

const renderCameraLink = (event: ExternalGeoEvent, t: TFunction): string => {
  if (
    event.data.externalSource !== 'alert_west_detection' ||
    !event.data.cameraId
  ) {
    return t('externalGeoEvent.unknown');
  }

  return `${ALERT_WEST_CAMERA_URL_BASE}${event.data.cameraId}`;
};

const createCameraLink = (event: ExternalGeoEvent): VoidFunc | undefined => {
  if (
    event.data.externalSource !== 'alert_west_detection' ||
    !event.data.cameraId
  ) {
    return undefined;
  }

  const { cameraId } = event.data;
  return () => {
    window.open(
      `${ALERT_WEST_CAMERA_URL_BASE}${cameraId}`,
      '_blank',
      'noopener,noreferrer',
    );
  };
};

const createInAppCameraLink = (
  event: ExternalGeoEvent,
): VoidFunc | undefined => {
  if (
    event.data.externalSource !== 'alert_west_detection' ||
    !event.data.cameraAppUrl
  ) {
    return undefined;
  }

  const { cameraAppUrl } = event.data;
  return () => {
    window.open(cameraAppUrl, '_blank', 'noopener,noreferrer');
  };
};

const createSatelliteImageryLink = (
  event: ExternalGeoEvent,
): VoidFunc | undefined => {
  if (event.data.externalSource === 'ngfs') {
    const { imageSourceLink, sourceUrl } = event.data;
    const link = imageSourceLink || sourceUrl;
    if (link) {
      return () => {
        window.open(link, '_blank', 'noopener,noreferrer');
      };
    }
  }
  return undefined;
};

const renderNotes = (
  event: ExternalGeoEvent,
  t: TFunction,
): string | ReactNode => {
  if (event.data.externalSource !== 'wea_pbs_warn') {
    return t('externalGeoEvent.unknown');
  }
  return (
    <>
      {event.data.notes.map((note) => (
        <Typography key={note} sx={{ pb: 2 }}>
          {note}
        </Typography>
      ))}
    </>
  );
};

const renderForecastStatus = (
  event: ExternalGeoEvent,
  t: TFunction,
): string => {
  if (event.data.externalSource !== 'spot_forecast' || !event.data.status) {
    return t('externalGeoEvent.unknown');
  }

  return event.data.status === 'ST_COMPLETE'
    ? t('externalGeoEvent.completed')
    : event.data.status;
};

const common: Record<string, ExternalGeoEventField> = {
  acreage: {
    label: 'externalGeoEvent.fields.acreage',
    renderer: renderAcreage,
  },
  burnType: { label: 'externalGeoEvent.fields.type', key: 'burnType' },
  category: { label: 'externalGeoEvent.fields.category', key: 'category' },
  containment: {
    label: 'externalGeoEvent.fields.containment',
    renderer: renderContainment,
  },
  incidentType: { label: 'externalGeoEvent.fields.type', key: 'incidentType' },
  severity: { label: 'externalGeoEvent.fields.severity', key: 'severity' },
  startDate: {
    label: 'externalGeoEvent.fields.date',
    renderer: renderStartDate,
  },
  startTime: {
    label: 'externalGeoEvent.fields.reported',
    renderer: renderStartTime,
  },
  status: { label: 'externalGeoEvent.fields.status', key: 'status' },
};

export const getFields = (event: ExternalGeoEvent): ExternalGeoEventField[] => {
  const result: ExternalGeoEventField[] = [];

  switch (event.externalSource) {
    case 'aircraft_detection':
      result.push({
        label: 'externalGeoEvent.fields.aircraft',
        key: 'aircraftName',
      });
      break;
    case 'alert_west':
      // Has no additional fields
      break;
    case 'alert_west_detection':
      result.push(
        {
          label: 'externalGeoEvent.fields.detailType',
          key: 'detailType',
        },
        {
          label: 'externalGeoEvent.fields.camera',
          key: 'cameraName',
          onClick: createInAppCameraLink(event),
        },
        {
          label: 'externalGeoEvent.fields.cameraLink',
          renderer: renderCameraLink,
          onClick: createCameraLink(event),
        },
        {
          label: 'externalGeoEvent.fields.detectionConfidence',
          key: 'detectionConfidence',
        },
      );
      break;
    case 'arkansas_forestry':
      result.push(
        common.startDate,
        common.acreage,
        {
          label: 'externalGeoEvent.fields.firingTechnique',
          key: 'firingTechnique',
        },
        {
          label: 'externalGeoEvent.fields.responsible',
          key: 'responsibleParty',
        },
        {
          label: 'externalGeoEvent.fields.availableFuels',
          key: 'availableFuels',
        },
      );
      break;
    case 'cal_fire':
      result.push(common.acreage, common.containment, {
        label: 'externalGeoEvent.fields.adminUnit',
        key: 'adminUnit',
      });
      break;
    case 'chp':
      result.push(common.incidentType, {
        label: 'externalGeoEvent.fields.department',
        key: 'dispatchCenter',
      });
      break;
    case 'dnr':
      result.push(
        common.startDate,
        common.acreage,
        { label: 'externalGeoEvent.fields.type', key: 'burnType' },
        { label: 'externalGeoEvent.fields.agency', key: 'agency' },
      );
      break;
    case 'fire_guard':
      result.push(
        {
          label: 'externalGeoEvent.fields.temperature',
          renderer: renderTemperature,
        },
        common.acreage,
        {
          label: 'externalGeoEvent.fields.analystComments',
          key: 'analystComments',
        },
      );
      break;
    case 'firesponse_fl':
    case 'firesponse_ga':
    case 'firesponse_ky':
    case 'firesponse_nc':
    case 'firesponse_sc':
    case 'firesponse_tn':
    case 'firesponse_va':
      result.push(
        common.acreage,
        common.containment,
        common.category,
        common.status,
        common.startTime,
      );
      break;
    case 'florida_forest_service':
      result.push(common.startDate, common.acreage, common.burnType);
      break;
    case 'mfc':
      result.push(
        {
          label: 'externalGeoEvent.fields.start',
          renderer: renderStartDatetime,
        },
        { label: 'externalGeoEvent.fields.end', renderer: renderEndDatetime },
        common.acreage,
        common.burnType,
        { label: 'externalGeoEvent.fields.purpose', key: 'burnPurpose' },
      );
      break;
    case 'n5_shield':
      result.push({
        label: 'externalGeoEvent.fields.type',
        key: 'description',
      });
      break;
    case 'ngfs':
      result.push(
        { label: 'externalGeoEvent.fields.type', key: 'incidentCategory' },
        { label: 'externalGeoEvent.fields.importance', key: 'importance' },
        { label: 'externalGeoEvent.fields.frp', key: 'frp' },
        {
          label: 'externalGeoEvent.fields.reported',
          renderer: renderReported,
        },
        {
          label: 'externalGeoEvent.fields.satelliteImagery',
          key: 'sourceLabel',
          onClick: createSatelliteImageryLink(event),
        },
      );
      if (event.data.relatedIncidentLink) {
        result.push({
          label: 'externalGeoEvent.fields.recentRelatedIncident',
          key: 'sourceLabel',
          onClick: () => {
            window.open(
              event.data.relatedIncidentLink,
              '_blank',
              'noopener,noreferrer',
            );
          },
        });
      }
      break;
    case 'nifc':
      result.push(
        common.acreage,
        common.containment,
        { label: 'externalGeoEvent.fields.type', renderer: renderFireType },
        common.startTime,
      );
      break;
    case 'nws_alert':
      result.push(
        { label: 'externalGeoEvent.fields.event', key: 'event' },
        { label: 'externalGeoEvent.fields.urgency', key: 'urgency' },
        common.severity,
        { label: 'externalGeoEvent.fields.certainty', key: 'certainty' },
        { label: 'externalGeoEvent.fields.damageThreat', key: 'damageThreat' },
        {
          label: 'externalGeoEvent.fields.start',
          renderer: renderStartDatetime,
        },
        { label: 'externalGeoEvent.fields.end', renderer: renderEndDatetime },
        { label: 'externalGeoEvent.fields.description', key: 'description' },
        { label: 'externalGeoEvent.fields.instruction', key: 'instruction' },
        { label: 'externalGeoEvent.fields.affected', key: 'affected' },
      );
      break;
    case 'nws_lsr':
      result.push(
        { label: 'externalGeoEvent.fields.event', key: 'typetext' },
        { label: 'externalGeoEvent.fields.source', key: 'source' },
        { label: 'externalGeoEvent.fields.wfo', key: 'wfo' },
        { label: 'externalGeoEvent.fields.fullReport', key: 'remark' },
      );
      break;
    case 'odf':
      result.push(common.startDate, common.acreage, common.burnType, {
        label: 'externalGeoEvent.fields.ownership',
        key: 'ownership',
      });
      break;
    case 'pfirs':
      result.push(
        common.status,
        common.startDate,
        common.acreage,
        common.burnType,
        {
          label: 'externalGeoEvent.fields.managingAgency',
          key: 'managingAgency',
        },
      );
      break;
    case 'pulsepoint':
      result.push(
        { label: 'externalGeoEvent.fields.type', key: 'typeName' },
        { label: 'externalGeoEvent.fields.units', key: 'unitCount' },
        { label: 'externalGeoEvent.fields.unitStatus', renderer: renderUnits },
        { label: 'externalGeoEvent.fields.department', key: 'agencyName' },
      );
      break;
    case 'noaa_river_gauge':
      result.push(
        {
          label: 'externalGeoEvent.fields.level',
          renderer: renderObservedLevel,
        },
        {
          label: 'externalGeoEvent.fields.forecastLevel',
          renderer: renderForecastLevel,
        },
        {
          label: 'externalGeoEvent.fields.forecast',
          key: 'forecastFloodCategory',
        },
        {
          label: 'externalGeoEvent.fields.flow',
          renderer: renderObservedFlow,
        },
        {
          label: 'externalGeoEvent.fields.forecastFlow',
          renderer: renderForecastFlow,
        },
      );
      break;
    case 'spot_forecast':
      result.push(
        {
          label: 'externalGeoEvent.fields.status',
          renderer: renderForecastStatus,
        },
        {
          label: 'externalGeoEvent.fields.date',
          key: 'localStr',
        },
      );
      break;
    case 'synoptic_precipitation':
      result.push(
        {
          label: 'externalGeoEvent.fields.accumulation',
          renderer: renderAccumulation,
        },
        {
          label: 'externalGeoEvent.fields.intensity',
          renderer: renderIntensity,
        },
        {
          label: 'externalGeoEvent.fields.threshold',
          renderer: renderThreshold,
        },
        {
          label: 'externalGeoEvent.fields.station',
          renderer: renderStation,
        },
      );
      break;
    case 'texas_am':
      result.push(
        common.acreage,
        common.containment,
        common.category,
        common.status,
        common.startTime,
      );
      break;
    case 'wea':
      result.push(common.severity);
      break;
    case 'wea_pbs_warn':
      result.push(
        { label: 'externalGeoEvent.fields.notes', renderer: renderNotes },
        { label: 'externalGeoEvent.fields.sender', key: 'sender' },
        { label: 'externalGeoEvent.fields.area', key: 'areaDescription' },
      );
      break;
    case 'wildcad':
      result.push(
        {
          label: 'externalGeoEvent.fields.incidentNumber',
          key: 'incidentNumber',
        },
        common.acreage,
        common.containment,
        common.incidentType,
        common.status,
        common.startTime,
      );
      break;
    case 'wi_dnr':
      result.push(
        common.acreage,
        {
          label: 'externalGeoEvent.fields.status',
          key: 'fireStatus',
        },
        {
          label: 'externalGeoEvent.fields.cause',
          key: 'generalCause',
        },
        {
          label: 'externalGeoEvent.fields.structuresThreatened',
          key: 'structureThreatened',
        },
      );
      break;
    default:
      break;
  }

  return result;
};

export const getHeadline = (event: ExternalGeoEvent, t: TFunction): string => {
  const { name } = event.data;
  switch (event.externalSource) {
    case 'aircraft_detection': {
      return event.data.isFiris
        ? t('externalGeoEvent.headlines.aircraftFiris', {
            aircraftName: event.data.aircraftName,
          })
        : t('externalGeoEvent.headlines.aircraft', {
            aircraftName: event.data.aircraftName,
          });
    }
    case 'alert_west':
    case 'alert_west_detection':
      return t('externalGeoEvent.headlines.alertWest', {
        cameraName: name,
      });
    case 'arkansas_forestry':
      return name
        ? t('externalGeoEvent.headlines.arkansasForestry', { name })
        : t('externalGeoEvent.headlines.arkansasForestryNoName');
    case 'cal_fire':
      return t('externalGeoEvent.headlines.calFire', { name });
    case 'chp':
      return event.data.address || t('externalGeoEvent.noAddress');
    case 'dnr':
      return t('externalGeoEvent.headlines.dnr', { name });
    case 'fire_guard':
      return t('externalGeoEvent.headlines.fireGuard', { name });
    case 'firesponse_fl':
    case 'firesponse_ga':
    case 'firesponse_ky':
    case 'firesponse_nc':
    case 'firesponse_sc':
    case 'firesponse_tn':
    case 'firesponse_va':
    case 'nifc':
    case 'texas_am':
    case 'wildcad': {
      const address = event.data.address || t('externalGeoEvent.noAddress');
      return t('externalGeoEvent.headlines.nameAndAddress', { address, name });
    }
    case 'florida_forest_service':
      return t('externalGeoEvent.headlines.ffs');
    case 'mfc':
      return t('externalGeoEvent.headlines.mfc');
    case 'n5_shield':
      return t('externalGeoEvent.headlines.n5Shield', { name });
    case 'ngfs':
      return t('externalGeoEvent.headlines.ngfs', { name });
    case 'nws_alert': {
      const { isActive, event: nwsEvent } = event.data;
      return isActive
        ? t('externalGeoEvent.headlines.nwsActive', { event: nwsEvent })
        : t('externalGeoEvent.headlines.nwsCancelled', { event: nwsEvent });
    }
    case 'nws_lsr':
      return event.data.city;
    case 'odf':
      return t('externalGeoEvent.headlines.odf', { name });
    case 'pfirs':
      return name;
    case 'pulsepoint': {
      const { isClosed, address } = event.data;
      if (!address) return '';
      return isClosed
        ? t('externalGeoEvent.headlines.pulsePointClosed', { address })
        : t('externalGeoEvent.headlines.pulsePoint', { address });
    }
    case 'noaa_river_gauge':
      return name;
    case 'spot_forecast':
      return t('externalGeoEvent.headlines.spotForecast', { name });
    case 'synoptic_precipitation':
      return t('externalGeoEvent.headlines.synoptic', { name });
    case 'wea':
      return t('externalGeoEvent.headlines.wea', { name });
    case 'wea_pbs_warn':
      return t('externalGeoEvent.headlines.weaPbsWarn', { name });
    case 'wi_dnr':
      return t('externalGeoEvent.headlines.wiDnr', { name });
    default:
      return event.data.address ?? '';
  }
};

const externalSourcesShownFloodingInfo: ExternalSource[] = [
  'chp',
  'nws_alert',
  'nws_lsr',
  'pulsepoint',
  'noaa_river_gauge',
  'synoptic_precipitation',
  'wea',
  'wea_pbs_warn',
];

const externalSourcesNotShownFireInfo: ExternalSource[] = [
  'nws_lsr',
  'noaa_river_gauge',
  'synoptic_precipitation',
];

export const showFloodingInfoForEvent = (event: ExternalGeoEvent): boolean =>
  externalSourcesShownFloodingInfo.includes(event.externalSource);

export const showFireInfoForEvent = (event: ExternalGeoEvent): boolean =>
  !externalSourcesNotShownFireInfo.includes(event.externalSource);
